// types
import type {
  CommunicationTemplate,
  CommunicationTemplateFormState,
  CommunicationTemplateType,
  CommunicationTemplateUsedFor
} from '@/types/domain/communicationTemplate'
// utilities
import { textToHtml } from '@/utilities/string/textToHtml'

/**
 * Editor-specific state used by the full-page communication template editor.
 */
interface CommunicationTemplateEditorState extends Omit<CommunicationTemplateFormState, 'body'> {
  body: string
}

/**
 * Serialized payload shape expected by the communications template API.
 */
interface SerializedCommunicationTemplatePayload {
  usedFor: CommunicationTemplateUsedFor | null
  name: string | null
  type: CommunicationTemplateType | null
  category: string | null
  subject: string | null
  body: string | null
  isDefault: boolean
  dealerships: number[] | null
}

/**
 * Returns true when the provided value contains non-whitespace text.
 */
function hasTextValue(value: unknown): boolean {
  return typeof value === 'string' ? value.trim().length > 0 : false
}

/**
 * Converts whitespace-only strings to null before sending them to the API.
 */
function trimToNull(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

/**
 * Extracts the primitive value from a local select model binding.
 */
function normalizeSelectValue<T extends string | number>(
  value: T | { value?: T | null } | null | undefined
): T | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'object') {
    return value.value ?? null
  }

  return value
}

/**
 * Extracts primitive values from a local multi-select model array.
 */
function normalizeMultiSelectValues<T extends string | number>(
  values: Array<T | { value?: T | null }> | null | undefined
): T[] {
  if (!Array.isArray(values)) {
    return []
  }

  return values
    .map(value => normalizeSelectValue<T>(value))
    .filter((value): value is T => value !== null)
}

/**
 * Normalizes line endings so body comparisons remain stable across platforms.
 */
function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n/g, '\n')
}

/**
 * Returns true when the provided string contains HTML markup.
 */
function containsHtmlMarkup(value: string | null | undefined): boolean {
  if (!hasTextValue(value)) {
    return false
  }

  return /<br\s*\/?>|<\/?[a-z][^>]*>/i.test(String(value))
}

/**
 * Decodes stored HTML into an editable plain-text string.
 */
function htmlToPlainText(value: string | null | undefined): string {
  if (!hasTextValue(value)) {
    return ''
  }

  const safeValue = String(value)
  const normalizedMarkup = normalizeLineEndings(safeValue)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|section|article|header|footer|li|tr|blockquote|h[1-6])>/gi, '\n')
    .replace(/<(li)(?:\s[^>]*)?>/gi, '- ')
    .replace(/<\/(ul|ol)>/gi, '\n')

  if (typeof document === 'undefined') {
    return normalizedMarkup.replace(/<[^>]+>/g, '').trim()
  }

  const container = document.createElement('div')
  container.innerHTML = normalizedMarkup

  return normalizeLineEndings(container.textContent ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Returns true when the editor HTML contains meaningful content.
 */
function hasMeaningfulHtml(value: string | null | undefined): boolean {
  if (!hasTextValue(value)) {
    return false
  }

  const safeValue = String(value)

  if (/<img[\s>]/i.test(safeValue)) {
    return true
  }

  return htmlToPlainText(safeValue).trim().length > 0
}

/**
 * Clones editor state so unsaved-change comparisons are safe.
 */
function cloneCommunicationTemplateEditorState(
  state: CommunicationTemplateEditorState
): CommunicationTemplateEditorState {
  return {
    ...state,
    dealerships: [...state.dealerships]
  }
}

/**
 * Normalizes an API template into the editor state shape.
 */
function normalizeCommunicationTemplateEditorState(
  template: CommunicationTemplate
): CommunicationTemplateEditorState {
  return {
    usedFor: template.usedFor,
    name: template.name,
    type: template.type,
    category: template.category,
    subject: template.subject,
    body: template.type === 'sms' ? normalizeLineEndings(template.body ?? '') : template.body ?? '',
    isDefault: !!template.isDefault,
    dealerships: Array.isArray(template.dealerships) ? [...template.dealerships] : []
  }
}

/**
 * Returns the body string that should be sent to the templates API.
 */
function serializeCommunicationTemplateBody(state: CommunicationTemplateEditorState): string | null {
  // SMS templates remain plain text because downstream send flows already handle them.
  if (state.type === 'sms') {
    return trimToNull(normalizeLineEndings(state.body))
  }

  // Email templates are always stored as HTML.
  return hasMeaningfulHtml(state.body) ? state.body.trim() : null
}

/**
 * Formats the current editor state into the payload expected by the templates API.
 */
function serializeCommunicationTemplatePayload(
  state: CommunicationTemplateEditorState
): SerializedCommunicationTemplatePayload {
  const usedFor = normalizeSelectValue<CommunicationTemplateUsedFor>(state.usedFor)
  const type = normalizeSelectValue<CommunicationTemplateType>(state.type)
  const dealerships = normalizeMultiSelectValues<number>(state.dealerships)

  return {
    usedFor,
    name: trimToNull(state.name),
    type,
    category: trimToNull(state.category),
    subject: type === 'email' ? trimToNull(state.subject) : null,
    body: serializeCommunicationTemplateBody(state),
    isDefault: state.isDefault,
    dealerships: dealerships.length ? dealerships : null
  }
}

/**
 * Prepares a stored email template body for immediate sending.
 */
function serializeEmailBodyForDelivery(value: string | null | undefined): string {
  if (!hasTextValue(value)) {
    return ''
  }

  const safeValue = String(value)

  if (containsHtmlMarkup(safeValue)) {
    return safeValue
  }

  return textToHtml(safeValue)
}

/**
 * Inserts a token into a plain-text value at the current cursor selection.
 */
function insertTokenAtSelection(
  value: string,
  token: string,
  selectionStart: number,
  selectionEnd: number
): { value: string; cursorPosition: number } {
  const safeStart = Math.max(0, selectionStart)
  const safeEnd = Math.max(safeStart, selectionEnd)
  const nextValue = `${value.slice(0, safeStart)}${token}${value.slice(safeEnd)}`

  return {
    value: nextValue,
    cursorPosition: safeStart + token.length
  }
}

export {
  cloneCommunicationTemplateEditorState,
  hasMeaningfulHtml,
  hasTextValue,
  htmlToPlainText,
  insertTokenAtSelection,
  normalizeCommunicationTemplateEditorState,
  serializeCommunicationTemplateBody,
  serializeCommunicationTemplatePayload,
  serializeEmailBodyForDelivery
}

export type { CommunicationTemplateEditorState, SerializedCommunicationTemplatePayload }
